import { pairs } from 'underscore';
import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Text, Label, Checkbox } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { withUnvalidatedControlErrors, withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { Alert } from '@bingads-webui-react/alert';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { FeedFilterPicker } from '@bingads-webui-campaign-react/feed-filter';
import { getStoreStyles } from '../styles/get-store-style';

const validate = (i18n, data) => {
  const validationErrors = [];

  if (data.IsFeedFiltered && !data.FeedLabel && !data.SalesCountry) {
    validationErrors.push(i18n.getString(_TL_('Either feed label or country/region of sale needs to be selected with the filter enabled. If you want to disable the filter, please click "Use all products" button.')));
  }

  return validationErrors;
};

function StoreSelectView(props) {
  const {
    stores,
    countries,
    validationProps,
    updateValidation,
    permissions,
  } = props;

  const { data } = validationProps;
  const {
    dynamic: {
      FeedLabel: feedLabelEnabled,
      FeedLabelBlockList: feedLabelBlockListEnabled,
    },
  } = permissions;
  const isFeedLabelEnabled = feedLabelEnabled && !feedLabelBlockListEnabled;

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStoreStyles);

  const onLocalChecked = useCallback((event) => {
    const isChecked = event.target.checked;
    const newData = {
      ...data,
      LocalInventoryAdsEnabled: isChecked,
    };

    updateValidation([], newData);
  }, [updateValidation, data]);

  const onDataChange = useCallback((dataPatch) => {
    const newData = {
      ...data,
      ...dataPatch,
    };
    const validationErrors = validate(i18n, newData);

    updateValidation(validationErrors, newData);
  }, [data, i18n, updateValidation]);

  const onIsFeedFilteredChange = useCallback((isFeedFiltered) => {
    onDataChange({
      IsFeedFiltered: isFeedFiltered,
      SalesCountry: isFeedFiltered ? data.SalesCountry : null,
      FeedLabel: isFeedFiltered ? data.FeedLabel : null,
    });
  }, [data.FeedLabel, data.SalesCountry, onDataChange]);

  const onSelectedFeedLabelChangeByValue = useCallback((value) => {
    onDataChange({
      FeedLabel: value,
    });
  }, [onDataChange]);

  const onSelectedSaleCountryChangeByValue = useCallback((value) => {
    onDataChange({
      SalesCountry: value,
    });
  }, [onDataChange]);

  const selectedStoreId = data.ProviderId;
  const selectedCountry = data.SalesCountry;

  const storeFieldId = useId('storeText');
  const countryFieldId = useId('countryText');
  const localInventoryFieldId = useId('localInventoryInput');

  return (
    <div className={classNames('store-settings-view', classes.storeSettings)}>
      <div className={classNames('strore-theme-info-pre-selected-items', classes.preSelectedItemsStyle)}>
        {selectedStoreId &&
          <div className="select-store-container">
            <div className={classNames(classes.select_label)}>
              <Text>{i18n.getString(_TL_('You will use this Merchant Center account to advertise products on your website.'))}</Text>
            </div>
            <Label htmlFor={storeFieldId}>
              {i18n.getString(_TL_('Store'))}
              <HelpPopup aria-label="Store" topic="pop_BA_PMAX_Store" />
            </Label>
            <div className={classes.control}>
              <Text id={storeFieldId} className="store-info-text">
                {/* If store has been deleted, or if the set store is a draft store, we show its ID without any additional info or formatting. The same as in the legacy shopping campaigns */}
                {(stores[selectedStoreId] && stores[selectedStoreId].text) || selectedStoreId}
              </Text>
            </div>
            {isFeedLabelEnabled ? (
              <FeedFilterPicker
                isEditMode
                isFeedFiltered={data.IsFeedFiltered}
                onIsFeedFilteredChange={onIsFeedFilteredChange}
                saleCountries={pairs(countries).map(([key, { text }]) => ({ key, text }))}
                selectedFeedLabel={data.FeedLabel}
                onSelectedFeedLabelChange={onSelectedFeedLabelChangeByValue}
                selectedSaleCountry={data.SalesCountry}
                onSelectedSaleCountryChange={onSelectedSaleCountryChangeByValue}
                storeId={selectedStoreId}
              />
            ) : (
              <>
                <Label htmlFor={countryFieldId}>
                  {i18n.getString(_TL_('Country/region of sale'))}
                </Label>
                <div className={classes.control}>
                  <Text id={countryFieldId} className="country-name-text">
                    {countries[selectedCountry].text}
                  </Text>
                </div>
              </>
            )}
            <Label htmlFor={localInventoryFieldId}>
              {i18n.getString(_TL_('Local inventory ads'))}
              <HelpPopup aria-label="Local" topic="pop_BA_PMAX_Local" />
            </Label>
            <Checkbox
              id={localInventoryFieldId}
              className={classNames('local-inventory', classes.control)}
              label={i18n.getString(_TL_('Enable local inventory ads'))}
              onChange={onLocalChecked}
              checked={data.LocalInventoryAdsEnabled}
            />
          </div>
        }

        {/* user doesn’t select store, then comes back to this step
        *View-only state for settings that can’t change */}
        {!selectedStoreId &&
          <div className={classNames('select-store-no-store', classes.warning_panel_bottom)}>
            <Alert
              message={i18n.getString(_TL_('This campaign can’t serve product ads because you didn’t select a Merchant Center store. If at some point you want to add a store, you’ll need to create a new campaign.'))}
              level="Info"
              icon={(<div className="alert-image" />)}
              dismissible={false}
            />
          </div>
        }
      </div>
    </div>
  );
}

StoreSelectView.propTypes = {
  stores: PropTypes.shape({}).isRequired,
  countries: PropTypes.shape({
    text: PropTypes.string,
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      IsFeedFiltered: PropTypes.bool,
      FeedLabel: PropTypes.string,
      SalesCountry: PropTypes.string,
      LocalInventoryAdsEnabled: PropTypes.bool,
      ProviderId: PropTypes.string,
    }).isRequired,
    alwaysShowError: PropTypes.bool,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      FeedLabel: PropTypes.bool,
      FeedLabelBlockList: PropTypes.bool,
    }),
  }).isRequired,
};

export const ValidatedStoreView = withError(withControlErrorHandling(withUnvalidatedControlErrors(withCustomValidation(withPermissions(StoreSelectView)))));
